import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Container,
  Box
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const EmpresaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    id: isEditMode ? id : uuidv4(),
    nombre: '',
    rfc: '',
    direccion_fiscal: '',
    telefono: '',
    email_compras: '',
    logo_url: '',
    pagina_web: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode) {
      const fetchEmpresa = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`/api/empresas/${id}`);
          setFormData(response.data);
          setError('');
        } catch (err) {
          setError('No se pudo cargar la empresa para editar.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchEmpresa();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setLogoFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (isEditMode) {
        const { id, ...updateData } = formData;
        await axios.put(`/api/empresas/${id}`, updateData);

        if (logoFile) {
          const fileData = new FormData();
          fileData.append('file', logoFile);
          await axios.post(`/api/empresas/${id}/logo`, fileData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        navigate(`/empresas/${id}`);
      } else {
        // For new companies, we can't upload a logo until it's created.
        // This could be a multi-step process, but for now, we'll just create the company.
        // The user can then edit the company to upload a logo.
        await axios.post('/api/empresas', formData);
        navigate('/empresas');
      }
    } catch (err) {
      setError('No se pudo guardar la empresa. Verifique los datos.');
      console.error(err);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Editar Empresa' : 'Crear Nueva Empresa'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            name="nombre"
            label="Nombre de la Empresa"
            value={formData.nombre}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            name="rfc"
            label="RFC"
            value={formData.rfc}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="direccion_fiscal"
            label="Dirección Fiscal"
            value={formData.direccion_fiscal}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="telefono"
            label="Teléfono"
            value={formData.telefono}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="email_compras"
            label="Email de Compras"
            type="email"
            value={formData.email_compras}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="pagina_web"
            label="Página Web"
            type="url"
            value={formData.pagina_web}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          {isEditMode && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Logo de la Empresa
              </Typography>
              <Button
                variant="contained"
                component="label"
              >
                Subir Logo
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {logoFile && <Typography variant="body2" sx={{ display: 'inline', ml: 2 }}>{logoFile.name}</Typography>}
              {formData.logo_url && !logoFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Logo actual: {formData.logo_url}
                </Typography>
              )}
            </Box>
          )}

          <Button type="submit" variant="contained" color="primary" sx={{ mt: 3, display: 'block' }}>
            {isEditMode ? 'Guardar Cambios' : 'Crear Empresa'}
          </Button>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

export default EmpresaForm;
